import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

import { GGTheme } from 'themes/types';

interface DevThemeSwitcherWrapperProps {
  onThemeOverride: (themeOverride: Partial<GGTheme> | null) => void;
}

// Conditionally load DevThemeSwitcher only in development mode
// This ensures zero production bundle impact
const DevThemeSwitcher = __DEV__
  ? dynamic(() => import('./DevThemeSwitcher'), {
      ssr: false, // Client-side only
      loading: () => null,
    })
  : null;

// Helper function to load stored theme override
const loadStoredThemeOverride = (): Partial<GGTheme> | null => {
  try {
    const storedOverride = localStorage.getItem('devThemeOverride');

    return storedOverride ? JSON.parse(storedOverride) : null;
  } catch (e) {
    console.warn('Failed to parse dev theme override:', e);
    return null;
  }
};

// Helper function to save theme override to localStorage
const saveThemeOverride = (theme: Partial<GGTheme> | null) => {
  if (theme) {
    localStorage.setItem('devThemeOverride', JSON.stringify(theme));
  } else {
    localStorage.removeItem('devThemeOverride');
  }
};

/**
 * Wrapper component that handles all dev theme switching logic.
 * Returns null when not in development mode or during SSR.
 * Encapsulates state management, localStorage persistence, and global function exposure.
 */
export const DevThemeSwitcherWrapper = ({
  onThemeOverride,
}: DevThemeSwitcherWrapperProps) => {
  const [devThemeOverride, setDevThemeOverride] =
    useState<Partial<GGTheme> | null>(null);

  const isNotDevOrSSR = !__DEV__ || typeof window === 'undefined';

  // Load initial theme override from localStorage
  useEffect(() => {
    if (isNotDevOrSSR) return;

    const storedOverride = loadStoredThemeOverride();

    if (storedOverride) {
      setDevThemeOverride(storedOverride);
    }
  }, [isNotDevOrSSR]);

  // Update parent when theme override changes
  useEffect(() => {
    if (isNotDevOrSSR) return;

    onThemeOverride(devThemeOverride);
  }, [devThemeOverride, onThemeOverride, isNotDevOrSSR]);

  // Expose global function for dev tools
  useEffect(() => {
    if (isNotDevOrSSR) return;

    const setTheme = (newTheme: Partial<GGTheme> | null) => {
      setDevThemeOverride(newTheme);
      saveThemeOverride(newTheme);
    };

    (window as any).__setDevThemeOverride = setTheme;

    return () => {
      delete (window as any).__setDevThemeOverride;
    };
  }, [isNotDevOrSSR]);

  // Return null if not in development mode or during SSR
  if (isNotDevOrSSR) {
    return null;
  }

  return DevThemeSwitcher ? <DevThemeSwitcher /> : null;
};
